
import { COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY, COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY } from '@plp/data-access/constants';
import { CompareBarProduct, CompareProductDetails } from '@shared/data-access/interfaces';
import { ComponentRef, inject, Inject, Injectable, Injector, PLATFORM_ID, runInInjectionContext, TemplateRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { SessionStorageService } from './session-storage.service';
import { isPlatformServer } from '@angular/common';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { ProductListCompareDao } from './product-list-compare/product-list-compare.dao';
import {
	setCompareProductDetails,
	setCompareSkeletonLoader,
	setCompareProductDetailsCallFailed,
	setCompareProducts,
	removeCompareProduct,
	addCompareProduct,
	setIsCompareFeatureHidden
} from '@plp/data-access/state/compare/compare.actions';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import { DeviceService } from '@shared/utils/services';
import { selectCompareProducts } from '@plp/data-access/state/compare/compare.selectors';
import { BreakPointNew } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class ProductListCompareService {
	private drawerRef: ComponentRef<HmfDrawerComponent> | null = null;
	private drawerContentRef: TemplateRef<unknown> | null = null;
	private modalFactoryService;
	private readonly paintedDoorTest = new Subject<void>();

	readonly paintedDoorTest$ = this.paintedDoorTest.asObservable();
	readonly readyToInit = new BehaviorSubject(false);
	readonly compareBarList$ = this.store.selectSignal(selectCompareProducts);

	constructor(
		private readonly store: Store,
		private readonly deviceService: DeviceService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly productListCompareDao: ProductListCompareDao,
		private readonly injector: Injector,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {}

	initCompareGrid(drawerContentRef: TemplateRef<unknown>): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		this.drawerContentRef = drawerContentRef;
		this.readyToInit.subscribe((ready) => {
			if (ready && !this.modalFactoryService) {
				runInInjectionContext(this.injector, () => {
					this.modalFactoryService = inject(HmfModalFactoryService);
				});
			}
		});
	}

	addProductToCompareList(compareProduct: CompareBarProduct): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(addCompareProduct({ compareProduct }));
	}

	removeProductFromCompareList(ecode: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(removeCompareProduct({ ecode }));
	}

	getCompareListFromSessionStorage(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const compareProducts: CompareBarProduct[] =
			JSON.parse(
				this.sessionStorageService.get(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '')
			) || [];

		this.store.dispatch(setCompareProducts({ compareProducts }));
	}

	updateSessionStorage(compareProducts: CompareBarProduct[]): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.set(
			COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
			JSON.stringify(compareProducts),
			''
		);
	}

	clearCompareList(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.remove(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '');
		this.store.dispatch(setCompareProducts({ compareProducts: [] }));
	}

	openPaintedDoorModal(): void {
		this.paintedDoorTest.next();
		this.sessionStorageService.set(COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY, 'true', '');
	}

	openCompareGrid(): void {
		const deviceType = this.deviceService.getDeviceType();

		this.drawerRef = this.modalFactoryService.create(HmfDrawerComponent, {
			direction: deviceType === 'desktop' ? 'right' : 'bottom',
			mobilePeek: deviceType !== 'desktop',
			size: this.setDeviceTypeAndDrawerWidth(),
			focus: false,
			closable: true,
			closableSide: 'right',
			isCloseButtonSticky: true,
			closableVariant: 'primary',
			headerText: 'Product Comparison',
	   }, this.drawerContentRef);

	   this.drawerRef.instance.closed.subscribe(() => {
		   this.drawerRef.destroy();
	   });

	   this.drawerRef.instance.openDrawer();
	}

	updateDrawerSettings(): void {
		const deviceType = this.deviceService.getDeviceType();

		this.drawerRef.instance.size = this.setDeviceTypeAndDrawerWidth();
		this.drawerRef.instance.direction = deviceType === 'desktop' ? 'right' : 'bottom';
		this.drawerRef.instance.mobilePeek = deviceType !== 'desktop';
	}

	hasTheComparePaintedDoorTestBeenOpened(): boolean {
		if (isPlatformServer(this.platformId)) {
			return true;
		}

		// if the storage key does not exist, the feature should be shown
		const shouldTheCompareFeatureBeHidden: string = this.sessionStorageService.get(COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY, '') || 'false';
		const hasThePaintedDoorTestBeenOpened = shouldTheCompareFeatureBeHidden === 'true';
		this.store.dispatch(setIsCompareFeatureHidden({ isCompareFeatureHidden: hasThePaintedDoorTestBeenOpened }));
		if (hasThePaintedDoorTestBeenOpened) {
			this.clearCompareList();
		}

		return hasThePaintedDoorTestBeenOpened;
	}

	getProductDetailsFromApi(ecodes: string[], selectedStore: string, isClearance: boolean): void {
		this.store.dispatch(setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: true }));

		//set the product details to state and loading state and error state
		this.productListCompareDao
			.getProductDetails(ecodes, selectedStore, isClearance)
			.pipe(take(1))
			.subscribe({
				next: (response: CompareProductDetails) => {
					this.store.dispatch(setCompareProductDetails({ productDetails: response }));
					this.store.dispatch(
						setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: false })
					);
					//TODO: This probably needs to be set after processing the data for the grid.
					this.store.dispatch(
						setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
					);
				},
				error: () => {
					this.store.dispatch(
						setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: true })
					);
					this.store.dispatch(
						setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
					);
				}
			});
	}

	private setDeviceTypeAndDrawerWidth(): 'large' | 'medium' | 'small' {
		if(isPlatformServer(this.platformId)) {
			return;
		}
		// If the user resizes or rotates their device, we need to maintain the hidden overflow
		const compareBarList = this.compareBarList$();

		const bp: BreakPointNew = this.deviceService.getDeviceBreakPointNew(false);

		if (bp <= BreakPointNew.MEDIUM) { // until 1024px
			return 'large';
		} else if (bp <= BreakPointNew.EXTRALARGE) {
			if (compareBarList.length === 2) {
				return 'medium';
			} else {
				return 'large';
			}
		} else if (compareBarList.length === 2) {
				return 'small';
		} else if (compareBarList.length === 3) {
			return 'medium';
		} else {
			return 'large';
		}
	}
}
